import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { haptic } from '@/lib/haptics';
import { ReUp, Product } from '@/types';

interface TodayRestocksCardProps {
  restocks: ReUp[] | undefined;
  products?: Product[];
  isToday?: boolean;
}

export function TodayRestocksCard({ restocks, products, isToday = true }: TodayRestocksCardProps) {
  const [expanded, setExpanded] = useState(false);

  if (!restocks || restocks.length === 0) {
    return null;
  }

  // Create product lookup map for fallback
  const productMap: Record<string, { name: string; category: string }> = {};
  products?.forEach(p => { productMap[p.id] = { name: p.name, category: p.category }; });

  // Calculate total restocked
  const totalQuantity = restocks.reduce((sum, r) => sum + r.quantity, 0);

  const formatTime = (date: Date | string) => {
    return new Date(date).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="rounded-2xl border-2 border-green-500/30 bg-green-500/5 overflow-hidden">
      {/* Header - Always visible */}
      <div
        className="p-4 flex items-center gap-4 cursor-pointer active:bg-white/5 active:scale-[0.99] transition-all"
        onClick={() => { haptic.light(); setExpanded(!expanded); }}
      >
        <div className="w-14 h-14 rounded-xl flex items-center justify-center text-3xl bg-green-500/20">
          📦
        </div>

        <div className="flex-1">
          <h3 className="font-bold text-white text-lg">{isToday ? "Today's" : "Day's"} Restocks</h3>
          <p className="text-sm text-gray-400">
            {restocks.length} restock{restocks.length !== 1 ? 's' : ''} • +{totalQuantity.toFixed(1)}g total
          </p>
        </div>

        <div className={cn(
          'text-xl text-gray-400 transition-transform',
          expanded && 'rotate-180'
        )}>
          ▼
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="px-4 pb-4 space-y-2 border-t border-green-500/20 pt-3">
          {restocks.map((restock) => {
            const productName = restock.product?.name || productMap[restock.productId]?.name || 'Unknown';

            return (
              <div
                key={restock.id}
                className="flex items-center gap-3 bg-dark-card rounded-xl p-3 border border-gray-700/50"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-white truncate">{productName}</div>
                  <div className="text-xs text-gray-500">
                    {restock.performedBy || 'Unknown'} • {formatTime(restock.createdAt)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-green-400">+{restock.quantity}g</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
